import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { BRAND } from "../lib/brand";
import { container, item } from "../lib/motion";
import { ResourceCardSkeleton } from "../components/Skeleton";
import { Clock, ArrowRight, ArrowLeft } from "lucide-react";
import { CATEGORIES, catMeta, articleImage } from "../data/resources";

function renderArticle(md) {
  if (!md) return { __html: "" };
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const blocks = md.split(/\n{2,}/);
  const html = blocks.map((b) => {
    const t = b.trim();
    if (t.startsWith("## ")) {
      return `<h3 class="font-head text-2xl font-medium mt-10 mb-4 tracking-tight text-white">${esc(t.slice(3))}</h3>`;
    }
    const inline = esc(t).replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-medium">$1</strong>');
    return `<p class="text-zinc-400 leading-[1.85] font-light text-[17px] mb-5">${inline.replace(/\n/g, "<br/>")}</p>`;
  }).join("");
  return { __html: html };
}

export default function Resources() {
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (cat) => {
    setLoading(true);
    const { data } = await api.get("/resources", { params: cat ? { category: cat } : {} });
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(null); }, []);
  const pick = (cat) => { setActive(cat); setSelected(null); load(cat); };

  return (
    <Layout glow="split" backdrop={BRAND.atmosphere}>
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
        <motion.div initial="hidden" animate="show" variants={container} className="text-center mb-16">
          <motion.span variants={item} className="eyebrow">Resource Library</motion.span>
          <motion.h1 variants={item} className="font-head text-4xl lg:text-6xl font-light mt-4 tracking-tight">Wisdom for the journey</motion.h1>
          <motion.p variants={item} className="text-zinc-500 mt-5 max-w-xl mx-auto font-light text-lg">Curated reflections to deepen your discipline, identity, and peace.</motion.p>
        </motion.div>

        <AnimatePresence mode="wait">
          {selected ? (
            <motion.article key="detail" initial={{ opacity: 0, y: 20, filter: "blur(8px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} exit={{ opacity: 0 }}
              className="max-w-2xl mx-auto" data-testid="resource-detail">
              <button onClick={() => setSelected(null)} data-testid="resource-back-top" className="text-sm text-zinc-400 hover:text-white flex items-center gap-1.5 mb-8 group">
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" /> Back to library
              </button>
              <div className="relative h-64 rounded-3xl overflow-hidden mb-10">
                <img src={articleImage(selected)} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#090909] via-[#090909]/55 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-8">
                  <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: catMeta(selected.category).color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: catMeta(selected.category).color }} /> {selected.category}
                  </span>
                  <h1 className="font-head text-3xl md:text-4xl font-light mt-3 tracking-tight leading-[1.1]">{selected.title}</h1>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-600 mb-8"><Clock className="w-3.5 h-3.5" /> {selected.read_minutes} min read</div>
              <p className="font-head text-xl md:text-2xl font-light text-zinc-200 leading-snug mb-10 tracking-tight">{selected.summary}</p>
              <div className="hairline mb-10" />
              <div dangerouslySetInnerHTML={renderArticle(selected.content)} />
              <div className="hairline mt-12 mb-8" />
              <button onClick={() => setSelected(null)} data-testid="resource-back-bottom" className="btn-glass px-6 py-3 text-sm flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Back to library
              </button>
            </motion.article>
          ) : (
            <motion.div key="list" initial="hidden" animate="show" variants={container}>
              {/* Category bento */}
              <motion.div variants={item} className="flex flex-wrap justify-center gap-2.5 mb-12">
                <button onClick={() => pick(null)} data-testid="resource-cat-all"
                  className={`px-5 py-2.5 rounded-full text-sm transition-all ${!active ? "bg-[#4da6ff] text-white glow-blue" : "btn-glass"}`}>All</button>
                {CATEGORIES.map((c) => (
                  <button key={c.name} onClick={() => pick(c.name)} data-testid={`resource-cat-${c.name.toLowerCase()}`}
                    className={`px-5 py-2.5 rounded-full text-sm transition-all flex items-center gap-2 ${active === c.name ? "bg-[#4da6ff] text-white glow-blue" : "btn-glass"}`}>
                    <c.icon className="w-4 h-4" strokeWidth={1.5} /> {c.name}
                  </button>
                ))}
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading && [0, 1, 2, 3, 4, 5].map((s) => <ResourceCardSkeleton key={s} />)}
                {!loading && items.map((r) => {
                  const meta = catMeta(r.category);
                  return (
                    <motion.div key={r.id} variants={item} onClick={() => setSelected(r)}
                      className="glass-card overflow-hidden cursor-pointer group" data-testid={`resource-${r.id}`}>
                      <div className="relative h-40 overflow-hidden">
                        <img src={articleImage(r)} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e12] via-[#0e0e12]/30 to-transparent" />
                        <div className="absolute bottom-4 left-5 flex items-center gap-2">
                          <meta.icon className="w-4 h-4" style={{ color: meta.color }} strokeWidth={1.5} />
                          <span className="text-[10px] font-semibold uppercase tracking-[0.15em]" style={{ color: meta.color }}>{r.category}</span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="font-head text-lg font-medium mb-2">{r.title}</h3>
                        <p className="text-zinc-500 text-sm leading-relaxed line-clamp-2 font-light">{r.summary}</p>
                        <div className="flex items-center justify-between mt-5 text-xs text-zinc-600">
                          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {r.read_minutes} min</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-[#4da6ff]" />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}
